import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock, Loader2, Send } from "lucide-react";
import { RechnungOption } from "@/components/RechnungOption";
import { DokumentVorschauDialog } from "@/components/DokumentVorschau";
import { JobProgress } from "@/components/JobProgress";

const errOf = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "object" && d ? d : { code: "", message: d || e?.message || "Senden fehlgeschlagen." };
};

// „Versandmitteilung senden“: Vorschau, Testmodus, Geschäftszeiten, Rechnung erstellen/mitsenden
export const VersandmitteilungDialog = ({ shipment, open, onOpenChange, onSent }) => {
  const [pv, setPv] = useState(null);
  const [rechnung, setRechnung] = useState(false);
  const [invPreview, setInvPreview] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [respectBh, setRespectBh] = useState(true);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState(null);
  const [job, setJob] = useState(null);
  const inFlight = useRef(false); // Doppelklick-Schutz (synchron, vor jedem Re-Render)

  useEffect(() => {
    if (!job || job.status !== "laeuft") return undefined;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/versand-jobs/${job.id}`);
        setJob(r.data);
        if (r.data.status === "fertig") {
          const d = r.data.result || {};
          const wann = d.scheduled_for ? ` – geplant für ${new Date(d.scheduled_for).toLocaleString("de-DE")}` : "";
          toast.success(`${d.test_mode ? "Testmail" : "Versandmitteilung"} eingereiht${d.mit_rechnung ? ` mit Rechnung ${d.rechnungsnummer}` : ""}${wann}.`);
          inFlight.current = false; onSent?.(d);
        } else if (r.data.status === "fehler") { inFlight.current = false; setFailure(r.data.error || { message: "Fehlgeschlagen." }); onSent?.(null); }
      } catch { setJob({ ...job }); }
    }, 1000);
    return () => clearTimeout(t);
  }, [job]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPreview = useCallback(async (mit, test = false) => {
    const r = await api.get(`/versand/${shipment.id}/versandmitteilung/vorschau`, { params: mit === undefined ? {} : { rechnung: mit, test_mode: test } });
    setPv(r.data);
    return r.data;
  }, [shipment?.id]);

  useEffect(() => {
    if (!open || !shipment) return;
    setPv(null); setFailure(null); setTestMode(false); setJob(null); inFlight.current = false;
    loadPreview().then((d) => { setRechnung(!!(d.rechnung.enabled && d.rechnung.default)); setRespectBh(!d.is_within_business_hours); })
      .catch((e) => { toast.error(errOf(e).message); onOpenChange(false); });
  }, [open, shipment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRechnung = (v) => { setRechnung(v); setFailure(null); loadPreview(v, testMode).catch(() => {}); };
  const toggleTest = (v) => { setTestMode(v); setFailure(null); loadPreview(rechnung, v).catch(() => {}); };

  const send = async (mitRechnung) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSending(true); setFailure(null);
    try {
      const r = await api.post(`/versand/${shipment.id}/versandmitteilung`, { test_mode: testMode, respect_business_hours: respectBh, rechnung: mitRechnung });
      setJob(r.data); onSent?.(null);
    } catch (e) { inFlight.current = false; setFailure(errOf(e)); }
    finally { setSending(false); }
  };
  const running = job?.status === "laeuft";
  const done = job?.status === "fertig";

  const to = testMode ? pv?.test_email : pv?.to_email;
  return (
    <>
    {shipment?.order_id && <DokumentVorschauDialog open={invPreview} onOpenChange={setInvPreview} orderId={shipment.order_id} typ="rechnung" busy={sending}
      onConfirm={async () => { setInvPreview(false); await send(true); }} />}
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="versandmitteilung-dialog">
        <DialogHeader><DialogTitle>Versandmitteilung senden{pv?.auftragsnummer ? ` · ${pv.auftragsnummer}` : ""}</DialogTitle></DialogHeader>
        {!pv ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
          <div className="space-y-3 text-sm">
            {!pv.smtp_configured && <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800" data-testid="vm-smtp-warning"><b>SMTP nicht konfiguriert</b> — die Mail kann nicht versendet werden.</div>}
            <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1">
              <dt className="text-slate-500">Empfänger</dt><dd className="font-mono break-all" data-testid="vm-to">{to || "(keine E-Mail hinterlegt)"}</dd>
              <dt className="text-slate-500">Betreff</dt><dd data-testid="vm-subject">{testMode ? `[TEST] ${pv.subject}` : pv.subject}</dd>
            </dl>
            <RechnungOption info={pv.rechnung} checked={rechnung} onChange={toggleRechnung} testMode={testMode} />
            <label className="p-3 rounded-md border-2 border-amber-300 bg-amber-50 flex items-center gap-3 cursor-pointer">
              <Switch checked={testMode} onCheckedChange={toggleTest} data-testid="vm-test-mode-switch" />
              <span className="font-medium text-amber-900">Test – an mich senden ({pv.test_email})</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <Switch checked={respectBh} onCheckedChange={setRespectBh} data-testid="vm-respect-bh-switch" />
              <span>Erst innerhalb Geschäftszeiten senden</span>
              {!pv.is_within_business_hours && pv.next_business_start_utc && <span className="text-xs text-amber-700 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> nächster Versand {new Date(pv.next_business_start_utc).toLocaleString("de-DE")}</span>}
            </label>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Vorschau</div>
              <iframe title="Vorschau Versandmitteilung" sandbox="" srcDoc={pv.html_body} className="w-full h-72 border rounded-md bg-white" data-testid="vm-preview" />
            </div>
            {job && (
              <div className={`p-3 rounded-md border space-y-2 ${done ? "border-emerald-200 bg-emerald-50" : job.status === "fehler" ? "border-rose-200 bg-rose-50" : "border-blue-200 bg-blue-50"}`} data-testid="vm-job" data-status={job.status}>
                <JobProgress job={job} testId="vm-job-steps" />
                {running && <p className="text-xs text-slate-600">Läuft im Hintergrund – Sie können den Dialog schließen; der Fortschritt bleibt im Auftrag sichtbar.</p>}
                {done && <p className="text-sm text-emerald-800" data-testid="vm-job-done">Fertig – die E-Mail ist eingereiht und steht in der E-Mail-Historie.</p>}
              </div>
            )}
            {failure && (
              <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-800 space-y-2" role="alert" data-testid="vm-error">
                <p>{failure.message}</p>
                {["rechnung_fehler", "rechnung_pdf_fehler", "rechnung_fertigstellen_fehler"].includes(failure.code) && (
                  <Button size="sm" variant="outline" onClick={() => { setJob(null); send(false); }} disabled={sending || running} data-testid="vm-send-without-invoice">Ohne Rechnung senden</Button>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vm-cancel">{running ? "Im Hintergrund weiter" : done ? "Schließen" : "Abbrechen"}</Button>
          {!done && (
            <Button onClick={() => (rechnung && !testMode && pv?.rechnung?.modus === "erstellen" && shipment?.order_id ? setInvPreview(true) : send(rechnung))} disabled={!pv || sending || running || !pv.smtp_configured || !to} data-testid="vm-send">
              {sending || running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {running ? (job.steps.some((st) => st.key === "rechnung" && st.status !== "ok") ? "Rechnung wird erstellt…" : job.steps.some((st) => st.key === "fertigstellen" && st.status === "laeuft") ? "Rechnung wird fertiggestellt…" : "Wird verarbeitet…") : pv?.gesendet_am && !testMode ? "Erneut senden" : "Senden"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
